import { formatReducedFraction } from "@/lib/option-group-portions"

/**
 * Base the decimal option quantities of one order item reduce against. Current orders carry each portioned
 * option as its fraction of one item within its own group (0.5, 0.667), so the base is 1 even when the item
 * has several portioned groups; only legacy slot quantities (1.5 + 1.5 of 3) reduce against their sum.
 */
export function getItemFractionBase(options: { quantity: number }[] | undefined): number | null {
  if (!options || options.length === 0) return null

  const decimalSelections = options.filter((option) => option.quantity > 0 && !Number.isInteger(option.quantity))
  if (decimalSelections.length === 0) return null
  if (decimalSelections.every((option) => option.quantity < 1)) return 1
  if (decimalSelections.length === 1) return null

  const total = decimalSelections.reduce((acc, option) => acc + option.quantity, 0)
  return total > 0 ? total : null
}

/**
 * Options whose `index` is absent (legacy orders) sink to the end while the
 * stable sort preserves their original relative order — the same rule
 * `build-checkout-cart-items.ts` applies when it stamps the index.
 */
const FALLBACK_INDEX = Number.MAX_SAFE_INTEGER

/**
 * Options of an order line, filtered to the ones actually selected and ordered
 * by their display `index` — the catalog sequence `buildCartItemOptionsPayload`
 * built at checkout (group by `OptionGroup.index`, then option by `Option.index`).
 *
 * The wire order is NOT trustworthy: order-service-core read the option rows
 * with no ORDER BY, so any update that physically moved a row (the merchant
 * enrichment reply, a POS edit) shuffled the array and a complement stamped
 * index 7 rendered in the middle of the list. The backend orders it now; this
 * keeps orders served by an older backend rendering right.
 */
export function getVisibleOptions<T extends { quantity: number; index?: number }>(
  options: T[] | undefined,
): T[] {
  if (!options) return []

  return options
    .filter((option) => option.quantity > 0)
    .sort((a, b) => (a.index ?? FALLBACK_INDEX) - (b.index ?? FALLBACK_INDEX))
}

/**
 * Label for an option quantity in order summaries: integers render as the
 * count plus `integerSuffix` (the orders list uses "x", the order detail
 * uses none); decimals render as a reduced fraction.
 */
export function formatOptionQuantityLabel(
  optionQuantity: number,
  fractionBase: number | null,
  { integerSuffix = "" }: { integerSuffix?: string } = {},
): string {
  if (Number.isInteger(optionQuantity)) return `${optionQuantity}${integerSuffix}`
  if (fractionBase) return formatReducedFraction(optionQuantity, fractionBase)
  return formatReducedFraction(optionQuantity, 1)
}
